import './AnnouncementsPage.css';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import ConfirmDialog from '../components/common/ConfirmDialog/ConfirmDialog';
import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import { useScoringAuth } from '../contexts/ScoringAuthContext';
import { useAnnouncements } from '../contexts/AnnouncementsContext';
import { scoringApp } from '../firebase';
import { MegaphoneIcon } from './TryoutIcons';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(date: Date): string {
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return timeStr;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${timeStr}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnnouncementsPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const { scoringUser } = useScoringAuth();
  const { announcements, create, clearAll } = useAnnouncements();
  const db = getFirestore(scoringApp);

  const [coachName, setCoachName] = useState('Coach');
  const [modalOpen, setModalOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // useEffect is appropriate here: one-time fetch of coach name on mount.
  useEffect(() => {
    if (!tryoutId || !scoringUser) return;
    let cancelled = false;

    getDoc(doc(db, 'tryouts', tryoutId, 'coaches', scoringUser.uid))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as Record<string, unknown>;
        if (typeof data.name === 'string' && data.name.trim()) {
          setCoachName(data.name.trim());
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [db, tryoutId, scoringUser]);

  const openModal = useCallback(() => {
    setDraftText('');
    setModalOpen(true);
    // Focus textarea on next frame after modal mounts.
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setDraftText('');
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = draftText.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await create(trimmed, coachName);
      closeModal();
    } finally {
      setSending(false);
    }
  }, [draftText, sending, create, coachName, closeModal]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      void handleSend();
    }
  }, [handleSend]);

  return (
    <div className="announcements-shell">
      <PageTopNav
        title="Announcements"
        onBack={() => navigate(`/tryout/${tryoutId}`)}
        backAriaLabel="Back to tryout"
      />

      <div className="announcements-content">
        {/* Make announcement button */}
        <button
          type="button"
          className="announcements-send-btn"
          onClick={openModal}
        >
          <span className="announcements-send-btn__icon">
            <MegaphoneIcon size={20} />
          </span>
          Make Announcement
        </button>

        {/* History */}
        {announcements.length === 0 ? (
          <div className="announcements-empty">
            <MegaphoneIcon size={32} />
            <p>No announcements yet</p>
          </div>
        ) : (
          <ul className="announcements-list">
            {announcements.map((a) => (
              <li key={a.id} className="announcement-card">
                <p className="announcement-card__text">{a.text}</p>
                <div className="announcement-card__meta">
                  <span className="announcement-card__author">{a.createdByName}</span>
                  <span className="announcement-card__sep" aria-hidden>·</span>
                  <span className="announcement-card__time">{formatTime(a.createdAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {announcements.length > 0 && (
          <button
            type="button"
            className="announcements-clear-btn"
            onClick={() => setClearConfirmOpen(true)}
          >
            Clear Announcements
          </button>
        )}
      </div>

      <ConfirmDialog
        open={clearConfirmOpen}
        title="Clear Announcements"
        message="Are you sure you want to delete all announcements?"
        confirmLabel="Delete All"
        cancelLabel="Cancel"
        onConfirm={() => { setClearConfirmOpen(false); void clearAll(); }}
        onCancel={() => setClearConfirmOpen(false)}
      />

      {/* Create announcement modal */}
      {modalOpen && (
        <div
          className="announcements-modal-overlay"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="announcements-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Make an announcement"
          >
            <div className="announcements-modal__header">
              <span className="announcements-modal__title">Make Announcement</span>
              <button
                type="button"
                className="announcements-modal__close"
                onClick={closeModal}
                aria-label="Cancel"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <textarea
              ref={textareaRef}
              className="announcements-modal__textarea"
              placeholder="e.g. 5 minutes until running tumbling starts"
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={280}
              rows={4}
            />
            <div className="announcements-modal__footer">
              <span className="announcements-modal__char-count">
                {draftText.length}/280
              </span>
              <button
                type="button"
                className="announcements-modal__send-btn"
                onClick={() => void handleSend()}
                disabled={!draftText.trim() || sending}
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
